import type { HomeGoal, Need, NextReview, Results } from "../../lib/ccApi";
import type { IconName } from "../../components/ui/Icon";
import { longDay as weekDay, planDay } from "../goal/text";

/**
 * Words the Command Center writes from data, ported from the prototype's own helpers (shortGoal, goalNoun,
 * pace, ccBrief). Nothing here is fixed copy about a goal: every number and name comes from the goal.
 */

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** The goal without its deadline or its event: "60 agents register". */
export const shortGoal = (t: string) =>
  String(t)
    .replace(/\s*\bby\b.*$/i, "")
    .replace(/\s+for (the|a|an|our)\b.*$/i, "")
    .trim();
/** Who or what the goal is about: "60 agents". */
export const goalNoun = (t: string) => shortGoal(t).replace(/\s+(register|registers|sign up|signs up|book|books|buy|buys|join|joins|reach|reaches)\b.*$/i, "");

/** A D1 time ("2026-10-01 10:48:22", UTC) or an ISO time as a Date. */
export const parseTime = (t: string) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(t) ? t : `${t.replace(" ", "T")}Z`);
/** A local day (YYYY-MM-DD) as a Date at local midnight. */
export const parseDay = (d: string) => {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
};
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "Today", "Yesterday" or "Mon 21 Sep", the prototype's day dividers. */
export function dayLabel(d: Date, now = new Date()): string {
  if (dayKey(d) === dayKey(now)) return "Today";
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayKey(d) === dayKey(y)) return "Yesterday";
  return `${WD[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`;
}
export const sameDay = (a: Date, b: Date) => dayKey(a) === dayKey(b);
/** "1 Sep". */
export const shortDay = (d: Date) => `${d.getDate()} ${MON[d.getMonth()]}`;
/** "22 September". */
export const longDay = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
/** "9:04", local. */
export const hm = (d: Date) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;

export const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
/** Cents as whole dollars, "$2,700"; with cents only when there are any. */
export const usd = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
/** Money spent so far, in whole dollars as the prototype shows totals: "$610". */
export const usd0 = (cents: number) => `$${Math.round(cents / 100).toLocaleString("en-US")}`;
/** What the agent's runs cost, in cents. */
export const agentCents = (r: Pick<Results, "agentCosts">) => Math.round((r.agentCosts.aiMicros + r.agentCosts.dataMicros + r.agentCosts.mediaMicros) / 10_000);
/** Everything spent on the goal: the ads, and the agent's AI and data, as the prototype counts it. */
export const totalSpent = (r: Pick<Results, "totals" | "agentCosts">) => r.totals.spendCents + agentCents(r);
/** Spend per result the way the prototype shows it: whole dollars ("$23"), with cents under $10 ("$3.07"). */
export const perResult = (spendCents: number, results: number) => {
  if (!results) return "none";
  const c = Math.round(spendCents / results);
  return c < 1000 ? `$${(c / 100).toFixed(2)}` : usd(Math.round(c / 100) * 100);
};
export const plural = (n: number, one: string, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
export const andList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

/** The first sentence of a message, for a thread's title. */
export function firstSentence(text: string, max = 52): string {
  const t = text.replace(/\s+/g, " ").trim() || "This message";
  const first = (t.match(/^.{12,}?[.?!](?=\s|$)/) ?? [t])[0].trim();
  return first.length > max ? `${first.slice(0, max - 3).trim()}...` : first;
}

// --- Where a goal stands -------------------------------------------------------------------------------

const PILLS: Record<string, [string, string]> = {
  setting_up: ["plain", "Setting up"],
  planning: ["live", "Planning"],
  plan_ready: ["tight", "Plan ready"],
  getting_ready: ["live", "Getting ready"],
  live: ["live", "Running"],
  paused: ["tight", "Paused"],
  done: ["good", "Done"],
};
/** A goal's state as the prototype's pill: its colour and its words. */
export const statePill = (state: string): [string, string] => PILLS[state] ?? ["plain", state.replace(/_/g, " ")];

/** Days from today to a local deadline (YYYY-MM-DD), at least 0. */
export function daysLeft(by: string, now = new Date()): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((parseDay(by).getTime() - today.getTime()) / 864e5));
}

export interface Pace {
  cls: "good" | "tight" | "bad";
  label: string;
  /** Results a week lately. */
  weekly: number;
  /** Results a week still needed. */
  need: number;
}

/** Pace from the last two weeks of results, the prototype's pace(): on pace at 15% above need, tight within 10% below. */
export function pace(results: Results, goal: { target: number; by: string } | null, now = new Date()): Pace | null {
  if (!goal) return null;
  const got = results.totals.results;
  const left = daysLeft(goal.by, now);
  if (left <= 0) return null;
  const days = results.days.toSorted((a, b) => a.day.localeCompare(b.day));
  const recent = days.slice(-14);
  if (!recent.length) return null;
  const weekly = (recent.reduce((a, d) => a + d.results, 0) / recent.length) * 7;
  const need = Math.max(0, goal.target - got) / (left / 7);
  const cls = weekly >= need * 1.15 ? "good" : weekly >= need * 0.9 ? "tight" : "bad";
  return { cls, label: cls === "good" ? "On pace" : cls === "tight" ? "Tight" : "Behind", weekly, need };
}

/** Results on the last full day before today, in the goal's own days. */
export function lastDay(results: Results, now = new Date()): number | null {
  const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const key = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
  const d = results.days.find((x) => x.day === key);
  return d ? d.results : null;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
/** "9:00" from "09:00". */
const clock = (t: string) => {
  const [h, m] = t.split(":");
  return `${Number(h)}:${m}`;
};
/** Days from one local day (YYYY-MM-DD) to another. */
const dayGap = (from: string, to: string) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 864e5);
/** The day and minute of the day now in a time zone; the viewer's own when the zone is unknown. */
function zoneNow(tz: string, now: Date): { day: string; min: number } {
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "numeric", minute: "numeric", hourCycle: "h23" })
        .formatToParts(now)
        .map((x) => [x.type, x.value]),
    );
    return { day: `${p.year}-${p.month}-${p.day}`, min: Number(p.hour) * 60 + Number(p.minute) };
  } catch {
    return { day: `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`, min: now.getHours() * 60 + now.getMinutes() };
  }
}
const inDays = (gap: number, weekday: number, at: string) => (gap <= 0 ? `today at ${at}` : gap === 1 ? `tomorrow at ${at}` : `on ${WEEKDAYS[weekday]} at ${at}`);

/** "tomorrow at 9:00", from `nextReview` on `GET /home`; `ahead` is in minutes, to find the soonest. */
export function reviewWhen(r: NextReview | null, now = new Date()): { when: string; ahead: number } | null {
  if (!r) return null;
  if ("at" in r) {
    const d = parseTime(r.at);
    const ahead = (d.getTime() - now.getTime()) / 60_000;
    if (ahead <= 1) return { when: "any minute", ahead: 0 };
    const gap = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 864e5);
    return { when: inDays(gap, d.getDay(), hm(d)), ahead };
  }
  const z = zoneNow(r.timezone, now);
  const gap = dayGap(z.day, r.day);
  const [h, m] = r.time.split(":").map(Number);
  return { when: inDays(gap, new Date(`${r.day}T12:00:00Z`).getUTCDay(), clock(r.time)), ahead: gap * 1440 + h * 60 + m - z.min };
}

/** The soonest next review across goals. */
export function soonestReview(goals: Pick<HomeGoal, "nextReview">[], now = new Date()): string | null {
  const all = goals.map((g) => reviewWhen(g.nextReview, now)).filter((x): x is { when: string; ahead: number } => !!x);
  return all.length ? all.toSorted((a, b) => a.ahead - b.ahead)[0].when : null;
}

// --- What waits for the owner --------------------------------------------------------------------------

const money = (v: unknown) => (typeof v === "number" ? usd(v) : "");
const str = (v: unknown) => (typeof v === "string" ? v : "");

/** A need in the owner's words: its icon, what it asks, and one line about it. */
export function needText(n: Pick<Need, "kind" | "label" | "payload" | "context">): { icon: IconName; title: string; line: string; kind: string } {
  const p = n.payload;
  const c = n.context ?? {};
  const ad = c.ad ? `“${c.ad.name}”` : null;
  const who = c.person ? c.person.name : null;
  const org = c.person?.org ?? "";
  switch (n.kind) {
    case "ad.add":
      return { icon: "flask", kind: "New ad idea", title: ad ? `Add ${ad} to the ad set` : n.label, line: str(p.reason) || "A new ad idea for the ad set. No extra money." };
    case "email.step": {
      const people = Array.isArray(p.people) ? p.people.length : (c.people?.length ?? 0);
      const names = (c.people ?? []).map((x) => x.name);
      const line = names.length ? (names.length > 2 ? `${names.slice(0, 2).join(", ")} and ${plural(names.length - 2, "more", "more")}` : andList(names)) : str(p.market);
      return { icon: "mail", kind: "Cold email", title: `Send email ${Number(p.step) || 1} to ${plural(people, "person", "people")}`, line };
    }
    case "email.reply":
      return {
        icon: "reply",
        kind: "Reply",
        title: who ? `Answer ${who}` : "Answer a reply",
        line: c.inbound?.subject ? `${org ? `${org} · ` : ""}${c.inbound.subject}` : "A draft is ready for your OK.",
      };
    case "goal.change":
      return { icon: "doc", kind: "A change", title: str(p.summary) || n.label, line: str(p.reason) || "A change to the plan or a setting." };
    case "audience.update":
      return { icon: "users", kind: "Who sees the ads", title: str(p.summary) || n.label, line: "Meta's learning starts again after this change." };
    case "placements.set":
      return { icon: "eye", kind: "Where the ads show", title: str(p.summary) || n.label, line: "Meta's learning starts again after this change." };
    case "budget.set":
      return { icon: "bolt", kind: "Daily budget", title: `Change the daily budget from ${money(p.fromCents)} to ${money(p.toCents)}`, line: str(p.reason) };
    case "campaign.create":
      return { icon: "bolt", kind: "Launch", title: n.label, line: "Nothing spends until you turn the ads on." };
    case "campaign.turn_on":
      return { icon: "play", kind: "Launch", title: n.label, line: "The campaign starts spending." };
    case "ad.text":
      return { icon: "pen", kind: "Ad text", title: ad ? `New text for ${ad}` : n.label, line: str(p.headline) };
    case "ad.pause":
      return { icon: "pause", kind: "Ad", title: ad ? `Pause ${ad}` : n.label, line: str(p.reason) };
    case "email.lead_pause":
      return { icon: "pause", kind: "Cold email", title: who ? `Stop emailing ${who}` : n.label, line: str(p.reason) || org };
    case "email.pause":
      return { icon: "pause", kind: "Cold email", title: n.label, line: str(p.reason) };
    case "email.resume":
      return { icon: "play", kind: "Cold email", title: n.label, line: str(p.reason) };
    case "email.list":
      return { icon: "users", kind: "Cold email", title: n.label, line: str(p.market) };
    default:
      return { icon: "user", kind: "Needs you", title: n.label, line: str(p.reason) };
  }
}

// --- When the ads go live ---------------------------------------------------------------------------

/** When the ads go live, as the prototype writes it ("Wednesday 7 October"), from the plan's own words. */
export function adsDay(g: Pick<HomeGoal, "adsStart">, now = new Date()): string | null {
  const d = g.adsStart ? planDay(g.adsStart.when, now) : null;
  return d ? weekDay(d) : null;
}
